import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ADMIN } from "@/constants/testIds";
import { Lock, MailCheck, ArrowLeft } from "lucide-react";

// Step 1 of password recovery: ask for the admin email and have Firebase send
// the reset link. The outcome is deliberately identical whether or not the
// address exists, so this form can't be used to discover admin accounts.
export default function ForgotPassword() {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      const code = err?.code || "";
      // An unknown address is reported exactly like a successful send —
      // anything else would leak which emails are admins.
      if (code === "auth/user-not-found") {
        setSent(true);
      } else if (code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (code === "auth/too-many-requests") {
        setError("Too many attempts. Please wait a few minutes and try again.");
      } else {
        setError("Couldn’t send the reset link. Please try again.");
        // eslint-disable-next-line no-console
        console.error("[admin-forgot]", err);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink px-6" data-testid={ADMIN.forgotPage}>
      <div className="w-full max-w-md border border-bronze/30 bg-cream p-10">
        <div className="flex items-center gap-3 text-bronze">
          <Lock size={18} />
          <span className="text-[0.7rem] tracking-[0.28em] uppercase">A Homely Abode · Admin</span>
        </div>

        {sent ? (
          <>
            <h1 className="font-serif text-3xl text-ink mt-4">Check your inbox</h1>
            <div
              data-testid={ADMIN.forgotSent}
              className="mt-6 flex gap-3 border border-bronze/30 bg-beige/40 px-4 py-4 text-sm text-ink"
            >
              <MailCheck size={18} className="shrink-0 text-bronze mt-0.5" />
              <p>
                If that email is registered, a password reset link has been sent to it. The link
                expires in one hour and can be used once.
              </p>
            </div>
            <p className="text-sm text-olive mt-4">
              Didn’t receive it? Check your spam folder, or{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="text-bronze underline underline-offset-2 hover:text-ink"
              >
                try another email
              </button>
              .
            </p>
            <Link
              to="/admin"
              className="mt-8 inline-flex items-center gap-2 text-sm text-olive hover:text-bronze"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-serif text-3xl text-ink mt-4">Reset your password</h1>
            <p className="text-sm text-olive mt-1">
              Enter your admin email and we’ll send you a link to set a new password.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <label className="block">
                <span className="editorial-label">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid={ADMIN.forgotEmail}
                  className="editorial-input"
                  autoComplete="username"
                  required
                />
              </label>

              {error && (
                <p
                  data-testid={ADMIN.forgotError}
                  className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid={ADMIN.forgotSubmit}
                className="btn-primary w-full disabled:opacity-60"
              >
                {busy ? "Sending…" : "Send Reset Link"}
              </button>
            </form>

            <Link
              to="/admin"
              className="mt-6 inline-flex items-center gap-2 text-sm text-olive hover:text-bronze"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
